import { useState } from "react";
import { Check } from "lucide-react";
import { useLeagueContext } from "@/modules/leagues/hooks/useLeagueContext";
import { useAvailableRounds, useMatchesByRound } from "@/modules/leagues/hooks/useMatches";
import { useRoundBonuses, useSetRoundBonus } from "@/modules/leagues/hooks/useRoundBonuses";
import { SettingsSubHeader } from "@/modules/leagues/components/SettingsSubHeader";
import { Skeleton } from "@/components/atoms/Skeleton";
import { Badge } from "@/components/atoms/Badge";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/utils/cn";
import { ROUND_BONUS_LABEL, type RoundBonusType } from "@/modules/leagues/types/roundBonus";

const BONUS_OPTIONS: Array<{ type: RoundBonusType | null; label: string }> = [
  { type: null, label: "Normal" },
  { type: "gol_de_placa", label: "Gol de Placa ×2" },
  { type: "gol_de_ouro", label: "Gol de Ouro ×3" },
];

/**
 * Config. da Rodada (itens 15-18) — o ADM escolhe, por partida, se ela vale
 * pontuação Normal, Gol de Placa (×2 no placar exato) ou Gol de Ouro (×3).
 * Exclusivo por partida (item 17) — trocar a seleção substitui a anterior.
 */
export function LeagueSettingsRoundConfigPage() {
  const { league } = useLeagueContext();
  const { toast } = useToast();
  const { data: rounds } = useAvailableRounds(league.id);
  const [selectedRound, setSelectedRound] = useState<number | undefined>(undefined);
  const { data: matches, isLoading: loadingMatches } = useMatchesByRound(league.id, selectedRound);
  const currentRound = selectedRound ?? matches?.[0]?.round;
  const { data: bonuses, isLoading: loadingBonuses } = useRoundBonuses(league.id, currentRound);
  const setBonus = useSetRoundBonus(league.id, currentRound);

  const bonusByMatchId = new Map((bonuses ?? []).map((b) => [b.matchId, b.type]));
  const selectedCount = bonuses?.length ?? 0;

  function handleSelect(matchId: string, type: RoundBonusType | null) {
    setBonus.mutate(
      { matchId, type },
      {
        onSuccess: () => toast({ title: type ? `${ROUND_BONUS_LABEL[type]} ativado` : "Voltou ao normal" }),
        onError: (err) =>
          toast({ title: "Não foi possível salvar", description: err instanceof Error ? err.message : undefined, variant: "destructive" }),
      }
    );
  }

  return (
    <div className="space-y-4">
      <SettingsSubHeader leagueId={league.id} title="Config. da rodada" />

      {rounds && rounds.length > 0 && (
        <select
          value={currentRound ?? ""}
          onChange={(e) => setSelectedRound(Number(e.target.value))}
          className="flex h-11 w-full rounded-xl border border-input bg-surface px-4 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {rounds.map((r) => (
            <option key={r.round} value={r.round}>
              {r.label}
            </option>
          ))}
        </select>
      )}

      <div className="flex items-center justify-between rounded-xl bg-muted/60 px-3 py-2 text-xs">
        <span className="text-muted-foreground">Bônus ativos nesta rodada</span>
        <Badge variant={selectedCount > 0 ? "accent" : "default"}>
          {selectedCount} partida{selectedCount !== 1 ? "s" : ""} selecionada{selectedCount !== 1 ? "s" : ""}
        </Badge>
      </div>

      {(loadingMatches || loadingBonuses) && (
        <div className="space-y-2">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      )}

      <div className="space-y-3">
        {matches?.map((match) => {
          const activeBonus = bonusByMatchId.get(match.id) ?? null;
          return (
            <div key={match.id} className="rounded-2xl border border-border bg-card p-3">
              <div className="mb-2 flex items-center justify-between text-xs text-muted-foreground">
                <span>
                  {match.homeTeam.shortName} × {match.awayTeam.shortName}
                </span>
                <span>{new Date(match.kickoff).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })}</span>
              </div>
              <div className="grid grid-cols-3 gap-1.5">
                {BONUS_OPTIONS.map((opt) => {
                  const isActive = activeBonus === opt.type;
                  return (
                    <button
                      key={opt.label}
                      onClick={() => handleSelect(match.id, opt.type)}
                      disabled={setBonus.isPending}
                      className={cn(
                        "flex items-center justify-center gap-1 rounded-lg border py-2 text-[11px] font-semibold transition-colors",
                        isActive
                          ? "border-accent-500 bg-accent-50 text-accent-700 dark:bg-accent-500/20 dark:text-accent-300"
                          : "border-border text-muted-foreground hover:bg-muted"
                      )}
                    >
                      {isActive && <Check className="h-3 w-3" />}
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
